<template>
  <div class="tfa-overlay" @click.self="$emit('close')">
    <div class="tfa-modal">
      <div class="tfa-head">
        <h3 class="tfa-title">Verificação em duas etapas (2FA)</h3>
        <button class="tfa-x" type="button" @click="$emit('close')">✕</button>
      </div>

      <div v-if="loading" class="tfa-loading">Carregando...</div>

      <!-- ── Códigos de recuperação (mostrados uma vez após ativar/regenerar) ── -->
      <template v-else-if="recoveryCodes.length">
        <p class="tfa-sub">Guarde estes <strong>códigos de recuperação</strong> num lugar seguro. Cada um funciona <strong>uma única vez</strong> caso você perca acesso ao app autenticador. Eles não serão mostrados de novo.</p>
        <div class="tfa-codes">
          <code v-for="c in recoveryCodes" :key="c" class="tfa-code">{{ c }}</code>
        </div>
        <div class="tfa-actions">
          <button class="tfa-btn tfa-btn--ghost" type="button" @click="copyCodes">{{ copied ? 'Copiado!' : 'Copiar' }}</button>
          <button class="tfa-btn tfa-btn--primary" type="button" @click="finishRecovery">Guardei, concluir</button>
        </div>
      </template>

      <!-- ── 2FA já ativo ── -->
      <template v-else-if="status && status.enabled">
        <div class="tfa-status tfa-status--on">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="20 6 9 17 4 12"/></svg>
          2FA ativo · {{ status.recoveryCodesRemaining }} código(s) de recuperação restante(s)
        </div>
        <p class="tfa-sub">O mesmo segredo pode estar em vários apps (celular e PC). Para adicionar um novo aparelho, desative e reative escaneando o QR em todos.</p>
        <div class="tfa-field">
          <label>Senha do administrador (para alterar o 2FA)</label>
          <input v-model="pwd" type="password" autocomplete="current-password" placeholder="Sua senha" />
        </div>
        <p v-if="error" class="tfa-error">{{ error }}</p>
        <div class="tfa-actions">
          <button class="tfa-btn tfa-btn--ghost" type="button" :disabled="busy" @click="regenRecovery">Gerar novos códigos</button>
          <button class="tfa-btn tfa-btn--danger" type="button" :disabled="busy" @click="disable2fa">Desativar 2FA</button>
        </div>
      </template>

      <!-- ── Fluxo de ativação (setup) ── -->
      <template v-else-if="setupData">
        <p class="tfa-sub">1. Escaneie o QR no seu app autenticador (Google Authenticator, Authy, 1Password...). <strong>Escaneie em todos os aparelhos que quiser</strong> (celular + PC).</p>
        <div class="tfa-qr-wrap">
          <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR 2FA" class="tfa-qr" />
        </div>
        <p class="tfa-manual">Ou digite a chave manualmente:<br><code>{{ setupData.secret }}</code></p>
        <div class="tfa-field">
          <label>2. Digite o código de 6 dígitos gerado pelo app</label>
          <input v-model="confirmCode" type="text" inputmode="numeric" placeholder="000000" @keyup.enter="enable2fa" />
        </div>
        <p v-if="error" class="tfa-error">{{ error }}</p>
        <div class="tfa-actions">
          <button class="tfa-btn tfa-btn--ghost" type="button" @click="cancelSetup">Cancelar</button>
          <button class="tfa-btn tfa-btn--primary" type="button" :disabled="busy" @click="enable2fa">Ativar 2FA</button>
        </div>
      </template>

      <!-- ── 2FA desativado (início) ── -->
      <template v-else>
        <div class="tfa-status tfa-status--off">2FA desativado</div>
        <p class="tfa-sub">Adicione uma segunda camada de proteção ao painel administrativo. Você vai precisar de um app autenticador no celular e/ou PC. Geramos códigos de recuperação para o caso de perder o aparelho.</p>
        <p v-if="error" class="tfa-error">{{ error }}</p>
        <div class="tfa-actions">
          <button class="tfa-btn tfa-btn--primary" type="button" :disabled="busy" @click="startSetup">Ativar 2FA</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue'
import QRCode from 'qrcode'
import { masterFetch } from '@/store/masterSession'

interface Status { enabled: boolean; recoveryCodesRemaining: number }

export default defineComponent({
  name: 'Admin2FAModal',
  emits: ['close', 'changed'],
  setup(_props, { emit }) {
    const loading = ref(true)
    const busy = ref(false)
    const error = ref('')
    const status = ref<Status | null>(null)
    const setupData = ref<{ secret: string; otpauthUri: string } | null>(null)
    const qrDataUrl = ref('')
    const confirmCode = ref('')
    const recoveryCodes = ref<string[]>([])
    const pwd = ref('')
    const copied = ref(false)

    const loadStatus = async () => {
      loading.value = true
      try {
        status.value = await masterFetch<Status>('/master/2fa/status')
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Erro ao carregar.'
      } finally {
        loading.value = false
      }
    }
    onMounted(loadStatus)

    const startSetup = async () => {
      busy.value = true; error.value = ''
      try {
        setupData.value = await masterFetch<{ secret: string; otpauthUri: string }>('/master/2fa/setup', { method: 'POST', body: '{}' })
        qrDataUrl.value = await QRCode.toDataURL(setupData.value.otpauthUri, { margin: 1, width: 200 })
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Erro ao iniciar.'
      } finally { busy.value = false }
    }
    const cancelSetup = () => { setupData.value = null; confirmCode.value = ''; error.value = '' }

    const enable2fa = async () => {
      if (!confirmCode.value.trim()) { error.value = 'Digite o código do app.'; return }
      busy.value = true; error.value = ''
      try {
        const r = await masterFetch<{ recoveryCodes: string[] }>('/master/2fa/enable', { method: 'POST', body: JSON.stringify({ token: confirmCode.value.trim() }) })
        recoveryCodes.value = r.recoveryCodes
        setupData.value = null
        emit('changed')
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Erro ao ativar.'
      } finally { busy.value = false }
    }

    const disable2fa = async () => {
      if (!pwd.value) { error.value = 'Informe a senha.'; return }
      if (!window.confirm('Desativar o 2FA? O painel voltará a exigir só a senha.')) return
      busy.value = true; error.value = ''
      try {
        await masterFetch('/master/2fa/disable', { method: 'POST', body: JSON.stringify({ currentPassword: pwd.value }) })
        pwd.value = ''
        emit('changed')
        await loadStatus()
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Erro ao desativar.'
      } finally { busy.value = false }
    }

    const regenRecovery = async () => {
      if (!pwd.value) { error.value = 'Informe a senha para gerar novos códigos.'; return }
      busy.value = true; error.value = ''
      try {
        const r = await masterFetch<{ recoveryCodes: string[] }>('/master/2fa/recovery-codes', { method: 'POST', body: JSON.stringify({ currentPassword: pwd.value }) })
        recoveryCodes.value = r.recoveryCodes
        pwd.value = ''
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Erro ao gerar códigos.'
      } finally { busy.value = false }
    }

    const copyCodes = async () => {
      try {
        await navigator.clipboard.writeText(recoveryCodes.value.join('\n'))
        copied.value = true
        setTimeout(() => { copied.value = false }, 1500)
      } catch { /* ignore */ }
    }

    const finishRecovery = async () => {
      recoveryCodes.value = []
      await loadStatus()
    }

    return {
      loading, busy, error, status, setupData, qrDataUrl, confirmCode, recoveryCodes, pwd, copied,
      startSetup, cancelSetup, enable2fa, disable2fa, regenRecovery, copyCodes, finishRecovery
    }
  }
})
</script>

<style scoped lang="scss">
.tfa-overlay { position: fixed; inset: 0; z-index: 620; background: rgba(15,23,42,.55); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; padding: 20px; }
.tfa-modal { background: #fff; border-radius: 14px; width: min(430px, 100%); max-height: 90vh; overflow-y: auto; padding: 22px 24px; box-shadow: 0 20px 60px rgba(0,0,0,.3); display: flex; flex-direction: column; gap: 12px; }
.tfa-head { display: flex; align-items: center; justify-content: space-between; }
.tfa-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; }
.tfa-x { background: none; border: none; font-size: 16px; color: #94a3b8; cursor: pointer; }
.tfa-loading { color: #64748b; font-size: 13px; padding: 10px 0; }
.tfa-sub { font-size: 12.5px; color: #475569; line-height: 1.55; margin: 0; }
.tfa-status { font-size: 13px; font-weight: 700; padding: 8px 12px; border-radius: 8px; display: flex; align-items: center; gap: 7px; }
.tfa-status--on { background: #f0fdf4; color: #15803d; }
.tfa-status--off { background: #f1f5f9; color: #64748b; }
.tfa-qr-wrap { display: flex; justify-content: center; padding: 8px 0; }
.tfa-qr { width: 200px; height: 200px; border: 1px solid #e2e8f0; border-radius: 8px; }
.tfa-manual { font-size: 12px; color: #64748b; text-align: center; margin: 0; code { display: inline-block; margin-top: 4px; background: #f1f5f9; padding: 4px 10px; border-radius: 6px; font-size: 13px; letter-spacing: 1px; color: #0f172a; word-break: break-all; } }
.tfa-field { display: flex; flex-direction: column; gap: 5px; }
.tfa-field label { font-size: 12px; font-weight: 600; color: #334155; }
.tfa-field input { padding: 9px 12px; border: 1.5px solid #d1d5db; border-radius: 8px; font-size: 15px; outline: none; letter-spacing: 2px; &:focus { border-color: #0f4c81; } }
.tfa-codes { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
.tfa-code { font-size: 13px; font-family: monospace; color: #0f172a; letter-spacing: 1px; text-align: center; }
.tfa-error { font-size: 12.5px; color: #dc2626; background: #fef2f2; padding: 7px 10px; border-radius: 6px; margin: 0; }
.tfa-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }
.tfa-btn { padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; border: none; &:disabled { opacity: .6; cursor: default; } }
.tfa-btn--ghost { background: #f1f5f9; color: #334155; &:hover:not(:disabled) { background: #e2e8f0; } }
.tfa-btn--primary { background: #0f4c81; color: #fff; &:hover:not(:disabled) { background: #0d3d6a; } }
.tfa-btn--danger { background: #dc2626; color: #fff; &:hover:not(:disabled) { background: #b91c1c; } }
</style>
